import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { CategoryGroup } from '@tech-insights/workflows-sdk';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import { TotalScoreDisplay } from '../TotalScoreDisplay/TotalScoreDisplay';
import { MiniScore } from '../MiniScore/MiniScore';
import { CategoryDetails } from '../CategoryDetails/CategoryDetails';
import { getDescription, roundTo2Decimals } from '../../util';

type Props = {
  categories: CategoryGroup[];
};

export const CategoryList = ({ categories }: Props) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <>
      <Typography variant="h4" color="text.secondary" mt={2}>
        Categories:
      </Typography>
      <Box>
        {categories?.map((category, index: number) => (
          <span key={`${category.name}-${index}`}>
            <Paper
              elevation={3}
              sx={{
                margin: 0,
                marginTop: '24px',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <Box sx={{ display: 'flex' }}>
                  <Box sx={{ width: '50%' }}>
                    <CardHeader title={category.name} />
                    <TotalScoreDisplay score={category.score ?? {}} />
                  </Box>
                  <Box
                    sx={{
                      width: '50%',
                      borderLeft: isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.5)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <CardHeader
                      title="Summary"
                      subheader="An overview of all checks this category contains"
                    />
                    <CardContent>
                      <List sx={{ width: '100%' }} disablePadding dense>
                        {category.executions?.map((exe, indexNumber) => (
                          <ListItem
                            key={`${exe?.contract?.name}-${indexNumber}`}
                          >
                            <Box
                              sx={{
                                alignSelf: 'flex-start',
                                width: '60%',
                                paddingRight: '8px',
                              }}
                            >
                              {exe.contract?.name}
                            </Box>
                            <Box
                              sx={{ display: 'flex', flexDirection: 'column' }}
                            >
                              <Box sx={{ display: 'flex' }}>
                                <Box sx={{ margin: '6px 12px 0 0' }}>
                                  <MiniScore
                                    achievementPercentage={
                                      exe.score?.achievementPercentage ?? 0
                                    }
                                  />
                                </Box>
                                <Typography variant="body2">
                                  {
                                    getDescription(
                                      exe.score?.achievementPercentage ?? 0,
                                    ).title
                                  }
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Typography
                                  sx={{
                                    marginLeft: '22px',
                                    color: isDarkMode
                                      ? 'rgba(255, 255, 255, 0.70)'
                                      : 'rgba(0, 0, 0, 0.60)',
                                  }}
                                  variant="body2"
                                >
                                  Current score:{' '}
                                  {roundTo2Decimals(
                                    exe.score?.achievementPercentage ?? 0,
                                  )}
                                  %
                                </Typography>
                              </Box>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Box>
                </Box>
              </Box>
            </Paper>
            <CategoryDetails category={category} />
          </span>
        ))}
      </Box>
    </>
  );
};
